/**
 * The project board: open-ended, paid work for a team that has finished the
 * course, posted by a company that exists.
 *
 * ## What this is, and what it deliberately is not
 *
 * A level is a problem with a known answer, graded in a container against
 * expected output held outside it. Everything in this product above the
 * terminal rests on that property — checkpoints, salted seeds, badges derived
 * and never stored (docs/verification.md).
 *
 * **A project has none of it, and that is the whole point.** There is no
 * expected answer, no grader, and no verdict a server can compute. Nobody is
 * holding a solution. Four people agree on one, argue about it, and something
 * either works or does not at the end of three weeks.
 *
 * That is the difference from level 142, and it is the difference that makes
 * every brief here worth taking, whoever posted it.
 *
 * ## One client, and it is real
 *
 * **The board was half practice until 2026-09-20.** Five of its eight briefs
 * were Lattice's — the fictional company the course is set in — and they were
 * unpaid exercises against a storefront that does not exist. They are gone.
 * What is left is only work somebody is paying for.
 *
 * That is a smaller board and a bigger claim, and the claim is the point: a
 * learner who finishes the course is not offered an exercise dressed as a
 * job. Every brief here is a real company's real gap, with a fee on it.
 *
 * **The machinery for a fictional client is deliberately still here** —
 * `ClientId` still has `'lattice'`, `CLIENTS` still records it as not real,
 * and `PracticeProject` still declares `terms?: never`. It costs one unused
 * record and it is what makes "a company that does not exist cannot pay
 * anybody" a compile error rather than a rule somebody remembers. The day a
 * practice brief is wanted again it is one entry in `PROJECTS`, and the guard
 * is already around it.
 *
 * **Blobor is a real company and its game is live at blobor.io.** It posts
 * its own work here under its own name and pays for a delivery it accepts.
 * Its briefs are grounded in gaps its own documents name — a dashboard nobody
 * has built over five committed SQL queries, an asset manifest with no
 * entries in it, and a share link the vision spends a page on.
 *
 * **Two briefs have been taken down, and both lessons are rules now.**
 *
 * `blobor-boot-budget` described work the client had already done: its
 * premise was true when it was drafted and false two days later, before any
 * team could have started. So "traceable to something the client wrote down"
 * is a rule about the day a brief is *posted*, not the day it was written —
 * **re-read the client's repository before every posting.**
 *
 * `blobor-load-harness` was the thing standing between Blobor and an unticked
 * line on its own MVP launch checklist: the Gate 1 report says in its verdict
 * that the gate needs "a harness that is not one Node process". That makes a
 * learner team's delivery load-bearing for the client, which the partner
 * conditions forbid. **Every brief must improve something that already works
 * or be a nice-to-have** — one that *unblocks* something is one the client is
 * waiting on, however politely it says otherwise.
 *
 * The one thing this file exists to keep true is that a reader can always
 * tell what they are looking at. `client` is a field on every project rather
 * than a convention in the copy, and `CLIENTS` says in one place whether that
 * company exists. While every brief is real that distinction draws nothing on
 * screen, and it stays anyway: the moment a learner cannot tell which of two
 * things in front of them is real, this product has spent the only thing it
 * sells.
 *
 * ## Two shapes that survived the repositioning
 *
 * **A brief is content, so it is bilingual in place.** Every project carries
 * `en` and `ar` copy of the same shape, and `ProjectCopy` on both is the
 * enforcement: a brief posted in English with no Arabic does not compile. The
 * course's other content uses `.ar.json` sidecars because it is large and
 * ships separately; three briefs are neither, and a sidecar would put the
 * translation one file away from the thing it translates, which is how a board
 * ends up with Arabic readers reading English titles again (`missionText`,
 * fixed 2026-09-16).
 *
 * **The board is a constant, not a table.** No admin surface, no D1 rows, no
 * editor. Posting a project is a commit and a deploy. The applications are
 * rows; the briefs are not.
 *
 * The Worker imports this to decide whether a slug names a real project
 * (worker/projects.ts); the page imports it to draw the board and the briefs
 * behind it (web/src/components/projects/). One source, so a slug the page can
 * link to is a slug the Worker will accept.
 */
/**
 * The companies, in the order the board draws them.
 *
 * Real work first. Not because practice is worth less — the five Lattice
 * briefs are the same five that shipped — but because a reader who scrolls
 * past paid work for a real company without noticing it has been failed by
 * the page, and a reader who reads the practice first and then finds out
 * there was money below it trusts the page less for the rest of the visit.
 */
export const CLIENTS = [
    {
        id: 'blobor',
        name: 'Blobor',
        real: true,
        site: 'blobor.io',
        comingSoon: true,
        hero: {
            path: 'shots/blobor-haven.webp',
            en: 'Blobor in play: Haven Outpost, a harvest walk under way, the map, and the shape you are in.',
            ar: 'Blobor أثناء اللعب: Haven Outpost، وجولة حصاد جارية، والخريطة، والشكل الذي أنتم فيه.',
        },
        what: {
            en: 'Our own game, live at blobor.io — open it and you are in it. We build it with AI agents, ' +
                'and qualified Operators can take small paid tasks on it at /tasks.',
            ar: 'لعبتنا نحن، وهي تعمل الآن على blobor.io — افتحوها وستكونون داخلها. نبنيها مع وكلاء الذكاء ' +
                'الاصطناعي، ويمكن لكل Operator مؤهَّل أخذ مهامّ صغيرة مدفوعة فيها على /tasks.',
        },
    },
    /*
     * No briefs since 2026-09-20, and kept for the guard rather than the copy.
     * Nothing below renders while `PROJECTS` holds none of Lattice's — the
     * board draws a section per client and skips the empty ones — so this is
     * the reserved id and the sentence that would go with it, waiting.
     */
    {
        id: 'lattice',
        name: 'Lattice',
        real: false,
        site: null,
        hero: null,
        comingSoon: false,
        what: {
            en: 'The company the course is set in, and it is fictional. Its briefs are the work it would ' +
                'plausibly need next: practice, unpaid, with nobody buying the result.',
            ar: 'الشركة التي تدور فيها أحداث المسار، وهي شركة خيالية. وأوصافها هي العمل الذي ستحتاج إليه على ' +
                'الأرجح: تمرين، بلا أجر، ولا أحد يشتري النتيجة.',
        },
    },
];
const CLIENT_BY_ID = new Map(CLIENTS.map((c) => [c.id, c]));
/**
 * The board: three, all Blobor's, all paid.
 *
 * The five Lattice briefs that used to sit beside them were removed on
 * 2026-09-20. They were good exercises and they were exercises, and a board
 * that mixes "somebody will pay you for this" with "nobody is waiting for
 * this" asks a reader to hold two ideas about the same page. Now it holds
 * one.
 *
 * **The three Blobor briefs** are grounded in sentences from Blobor's own
 * documents, not in things that would be nice for a real company to want. Its
 * `ops/README.md` says "no dashboard product sits between us and the numbers
 * yet"; its asset manifest still holds `{}`; the product vision spends a page
 * on the shareable link while the deployed origin answers a share code with a
 * bare redirect and serves no card tags at all. A brief for a real client
 * that is not traceable to something the client has written down is a brief
 * we invented and attributed to them — and one that *was* traceable when it
 * was drafted has to be checked again on the day it goes up.
 *
 * **And each is checked against the client's own critical path**, which is
 * the harder test and the one that took a brief down. The asset pipeline sits
 * under "Not in the MVP" in the client's plan; the share card is not on its
 * launch checklist at all and the link it improves already works; the
 * dashboard sits *beside* a checklist line rather than on it, because what
 * ticks "dashboards live" is running the five queries once against real
 * traffic (`ops/README.md`), which is a path that already exists. A brief
 * that unblocks a checklist item instead of improving a working one is a
 * brief the client is waiting on.
 *
 * Three properties held deliberately across the whole board:
 *
 * **Each is a different shape** — a page, a service, a command — so that a
 * team can pick the one they are. With one client the rule is simply the
 * board's: three briefs of one shape would be a board most teams have no
 * version of themselves on.
 *
 * **Each is buildable from nothing but the brief.** No repository to be
 * given, no credentials, no access. A team starts with an empty directory and
 * the description of a problem, which is the situation the course never puts
 * them in and the one that most resembles the job. This survived a real
 * client arriving, and it is why the Blobor briefs are around the game rather
 * than inside it: nobody is handed the monorepo, and nothing a team builds
 * can reach Blobor's players or their data.
 *
 * And each has at least one condition in `done` that is a judgement call
 * rather than a feature, because judgement is the thing the course spends a
 * hundred levels teaching and the thing a checklist cannot ask for.
 */
export const PROJECTS = [
    {
        slug: 'blobor-dashboards',
        client: 'blobor',
        terms: { fee: { minorUnits: 30_000, currency: 'USD' } },
        kind: 'frontend',
        weeks: 3,
        posted: '2026-09-19',
        closes: '2026-12-15',
        team: { min: 2, max: 5 },
        shot: {
            path: '',
            en: 'The five questions a live world is asked, and the terminal somebody is currently pasting SQL into to answer them.',
            ar: 'الأسئلة الخمسة التي يُسأل عنها عالم حيّ، والطرفية التي يلصق فيها أحدهم استعلامات SQL اليوم للإجابة عنها.',
        },
        given: [
            {
                en: 'The five queries as they stand today, as SQL, and the positional column contract they read — the rows are stored by position and the names exist nowhere in the data, so the contract is the only way to know what column four of a netcode row is.',
                ar: 'الاستعلامات الخمسة كما هي اليوم، بلغة SQL، ومع عقد الأعمدة الموضعي الذي تقرأ به — فالصفوف مخزَّنة بالموضع ولا وجود للأسماء في البيانات، والعقد هو السبيل الوحيد لمعرفة ما هو العمود الرابع في صف الشبكة.',
            },
            {
                en: 'The threshold each view is judged against: tick p99 at 25 ms, the average frame at 6 KB, a correction share of 0.95, and the four product numbers from the vision.',
                ar: 'العتبة التي يُحاكَم عليها كل عرض: المئين 99 للنبضة عند 25 مللي ثانية، ومتوسّط الإطار عند 6 كيلوبايت، ونسبة تصحيح 0.95، والأرقام المنتجية الأربعة من الرؤية.',
            },
            {
                en: 'The endpoint the queries are posted to, and a read-only token for a dataset with test traffic in it. Production is not yours to read and you will not be given it.',
                ar: 'الطرف الذي تُرسَل إليه الاستعلامات، ورمز قراءة فقط لمجموعة بيانات فيها حركة اختبارية. أما الإنتاج فليس لكم أن تقرؤوه ولن يُعطى لكم.',
            },
        ],
        sources: [
            {
                mission: 'l60-ask-the-data-a-question',
                en: 'A query is a question with a shape. Someone else already wrote these five.',
                ar: 'الاستعلام سؤال له بنية. وهذه الخمسة كتبها غيركم بالفعل.',
            },
            {
                mission: 'l76-count-it',
                en: 'What an average hides, and why a p99 is the number that decides.',
                ar: 'ما الذي يخفيه المتوسّط، ولماذا المئين 99 هو الرقم الحاسم.',
            },
            {
                mission: 'l79-do-not-ask-twice',
                en: 'A page that is expensive to open is a page nobody opens in an incident.',
                ar: 'صفحة فتحها مكلف هي صفحة لا يفتحها أحد أثناء العطل.',
            },
            {
                mission: 'l64-design-the-endpoint',
                en: 'A view that could not read must say so, not draw the last good answer.',
                ar: 'العرض الذي تعذّرت عليه القراءة عليه أن يقول ذلك، لا أن يرسم آخر إجابة صالحة.',
            },
        ],
        en: {
            title: 'One screen for the five questions',
            summary: 'Blobor has five queries it trusts and nowhere to run them. Build the screen.',
            why: [
                'Everything Blobor knows about a running world is five questions: is it holding up, does it ' +
                    'feel connected, who is being hurt, did the world do its job, and what is it costing. The ' +
                    'queries exist, committed, each with a note above it saying what bad looks like, and since ' +
                    'the game went live the telemetry really is landing in the dataset they read. What does not ' +
                    'exist is anywhere to run them. Somebody pastes SQL into a terminal the morning after a ' +
                    'playtest, which means nobody who is not an engineer ever sees a number — and during an ' +
                    'incident, the person who most needs the first query is the person holding the incident.',
                'What makes it three weeks is that the numbers do not all arrive the same way. Some come from ' +
                    'an analytics store queried over HTTP with SQL and some from a database; some questions are ' +
                    'one number and some are a distribution; and a page that re-reads everything every thirty ' +
                    'seconds costs money per view on a product whose cost is one of the five things it reports. ' +
                    'Deciding what refreshes, what is cached and what a reader has to ask for is the design, and ' +
                    'it is most of the work.',
            ],
            build: [
                'One page, five views, one per question, each naming the question in words before it draws anything.',
                'A window a reader chooses — the last hour, the last playtest, the last week — that every view answers for.',
                'Distributions drawn as distributions. A p99 tick time and a correction histogram are the two numbers that decide whether the world is playable, and an average hides both.',
                'Configuration, not credentials in the source: an account, a dataset and a token it is pointed at. It ships with a generator that makes a plausible day of data, so the page can be built, demonstrated and reviewed with no account at all.',
                'Every view says when it read. A view that failed to read says that, rather than drawing the last good answer as though it were current.',
            ],
            done: [
                'From a clean clone, one command against the fixture generator draws all five views, with no account and no network.',
                'The safety view never shows a message body, an email or a name. Counts, reasons and ids only — this is the one view built on rows about people who were hurt, and it is the one that must never become a way to read them.',
                'One store being unreadable costs one view. The page names which of the five could not answer and the rest still works.',
                'Opening the page costs a bounded number of queries and you can say the number. A dashboard that is expensive to look at is a dashboard nobody opens during the incident it was built for.',
            ],
            notIncluded: [
                'Alerting of any kind. Reporting state is the job; waking somebody up is a different one.',
                'Writing anything back. No kicking a player, no draining a cell, no editing a row — the screen reads.',
                'Anything beyond one shared secret at the door. How Blobor puts this behind its own front door is Blobor’s.',
            ],
        },
        ar: {
            title: 'شاشة واحدة للأسئلة الخمسة',
            summary: 'لدى Blobor خمسة استعلامات تثق بها ولا مكان تشغّلها فيه. ابنوا ذلك المكان.',
            why: [
                'كل ما تعرفه Blobor عن عالم يعمل هو خمسة أسئلة: هل يصمد، وهل يبدو متّصلًا، ومن الذي يُؤذى، وهل ' +
                    'أدّى العالم وظيفته، وكم يكلّف. الاستعلامات موجودة ومحفوظة، وفوق كلٍّ منها ملاحظة تصف كيف يبدو ' +
                    'السوء، ومنذ صارت اللعبة حيّة صارت القياسات تصل فعلًا إلى المجموعة التي تقرؤها. وما لا وجود له ' +
                    'هو مكان تشغّلها فيه. يلصق أحدهم استعلامًا في طرفية صباح اليوم التالي ' +
                    'للتجربة، ومعنى ذلك أن أحدًا من غير المهندسين لا يرى رقمًا قط — وأثناء العطل يكون أحوج الناس ' +
                    'إلى الاستعلام الأول هو نفسه المنشغل بالعطل.',
                'وما يجعله ثلاثة أسابيع أن الأرقام لا تصل كلها من طريق واحد. بعضها من مخزن تحليلات يُسأل عبر HTTP ' +
                    'بلغة SQL وبعضها من قاعدة بيانات؛ وبعض الأسئلة رقم واحد وبعضها توزيع؛ وصفحة تعيد قراءة كل شيء ' +
                    'كل ثلاثين ثانية تكلّف مالًا في كل فتحة، على منتج تكلفتُه واحدة من الخمسة التي تعرضها. وتحديد ما ' +
                    'الذي يُحدَّث وما الذي يُخزَّن وما الذي يطلبه القارئ بنفسه هو التصميم، وهو معظم العمل.',
            ],
            build: [
                'صفحة واحدة، وخمسة عروض، عرض لكل سؤال، كلٌّ منها يذكر سؤاله بالكلمات قبل أن يرسم شيئًا.',
                'نافذة زمنية يختارها القارئ — الساعة الماضية، أو آخر تجربة، أو الأسبوع الماضي — ويجيب عنها كل عرض.',
                'التوزيعات تُرسم توزيعاتٍ. المئين 99 لزمن النبضة ومدرّج التصحيحات هما الرقمان اللذان يقرّران إن كان العالم قابلًا للعب، والمتوسّط يخفيهما معًا.',
                'إعدادات لا أسرار في الشيفرة: حساب ومجموعة بيانات ورمز يُوجَّه إليها. ويأتي معها مولّد يصنع يومًا معقولًا من البيانات، حتى تُبنى الصفحة وتُعرض وتُراجَع بلا حساب أصلًا.',
                'كل عرض يقول متى قرأ. والعرض الذي تعذّرت عليه القراءة يقول ذلك، بدل أن يرسم آخر إجابة صالحة كأنها الحالية.',
            ],
            done: [
                'من نسخة نظيفة، أمر واحد مقابل مولّد البيانات يرسم العروض الخمسة كلها، بلا حساب وبلا شبكة.',
                'عرض السلامة لا يُظهر نصّ رسالة ولا بريدًا ولا اسمًا. أعدادًا وأسبابًا ومعرّفات فقط — فهذا هو العرض الوحيد المبنيّ على صفوف تخصّ أناسًا وقع عليهم أذى، وهو الذي يجب ألّا يصير بابًا لقراءتهم.',
                'تعذُّر قراءة مخزن يكلّف عرضًا واحدًا. تسمّي الصفحة أيّ الخمسة لم يُجب، ويظل الباقي عاملًا.',
                'فتح الصفحة يكلّف عددًا محدودًا من الاستعلامات، وتستطيعون ذكر العدد. فلوحةٌ فتحها مكلف هي لوحة لا يفتحها أحد أثناء العطل الذي بُنيت له.',
            ],
            notIncluded: [
                'التنبيهات بأي شكل. الإبلاغ عن الحالة هو العمل؛ وإيقاظ الناس عمل آخر.',
                'الكتابة في أي اتجاه عكسي. لا طرد لاعب ولا تفريغ خليّة ولا تعديل صف — الشاشة تقرأ.',
                'أي شيء أبعد من سرٍّ واحد على الباب. أما كيف تضع Blobor هذا خلف بابها فذلك شأنها.',
            ],
        },
    },
    {
        slug: 'blobor-share-card',
        client: 'blobor',
        terms: { fee: { minorUnits: 30_000, currency: 'USD' } },
        kind: 'service',
        weeks: 3,
        posted: '2026-09-19',
        closes: '2026-12-15',
        team: { min: 2, max: 5 },
        shot: {
            path: '',
            en: 'The same link in three places: a chat app, a social post and a plain inbox — one of them showing nothing at all.',
            ar: 'الرابط نفسه في ثلاثة مواضع: تطبيق محادثة، ومنشور اجتماعي، وبريد عادي — وأحدها لا يُظهر شيئًا البتة.',
        },
        given: [
            {
                en: 'What the game knows at the moment somebody is eaten — the fields it can hand you and the ones it cannot — so the shape you design is one it can actually fill.',
                ar: 'ما تعرفه اللعبة لحظة يُؤكل أحدهم — الحقول التي تستطيع إعطاءكم إيّاها والتي لا تستطيع — حتى تصمّموا بنية تقدر اللعبة على ملئها فعلًا.',
            },
            {
                en: 'The URL that puts a player into a named world, which is the one thing your page has to get right. Everything else about the live site you can read from outside: open blobor.io and look.',
                ar: 'العنوان الذي يُدخل لاعبًا إلى عالم بعينه، وهو الشيء الوحيد الذي يجب أن تصيبه صفحتكم. وكل ما عدا ذلك في الموقع الحيّ تقرؤونه من الخارج: افتحوا blobor.io وانظروا.',
            },
        ],
        sources: [
            {
                mission: 'l68-the-key-in-the-history',
                en: 'A player’s name is something a stranger typed, and this draws it into an image and into HTML.',
                ar: 'اسم اللاعب شيء كتبه غريب، وهذا يرسمه داخل صورة وداخل HTML.',
            },
            {
                mission: 'l79-do-not-ask-twice',
                en: 'A crawler asks once, caches for days, and never comes back to see if you fixed it.',
                ar: 'الزاحف يسأل مرّة، ويخزّن أيامًا، ولا يعود ليرى إن كنتم قد أصلحتم شيئًا.',
            },
            {
                mission: 'l56-ask-the-server-yourself',
                en: 'Something with a one-second patience is about to decide whether anybody clicks.',
                ar: 'شيءٌ صبره ثانية واحدة هو الذي سيقرّر إن كان أحد سيضغط.',
            },
            {
                mission: 'l43-behind-a-flag',
                en: 'Where the second goes, and what can be done before the request arrives.',
                ar: 'أين تذهب الثانية، وما الذي يمكن إنجازه قبل وصول الطلب.',
            },
        ],
        en: {
            title: 'The link that says what happened',
            summary: 'A short link with a moment behind it: a preview that reads in a feed, and a page that puts the reader in the same world.',
            why: [
                'Blobor’s distribution plan is one sentence: a clip should explain the game before the viewer ' +
                    'knows its name. When a player is eaten the game already knows enough to say it — who ate ' +
                    'them, how long they lasted, which world. What gets shared today is a bare URL: blobor.io ' +
                    'answers a share code with a redirect into the world, which is right for a person and is ' +
                    'nothing at all to a crawler — the live origin serves no card tags on any page. Pasted into ' +
                    'a chat the link unfurls as nothing, and the person receiving it is being asked to trust a ' +
                    'stranger’s link with no idea what is on the other side of it.',
                'Three weeks because the thing you are building for is a machine you will never see. A preview ' +
                    'is fetched by somebody else’s crawler, on its schedule, with no JavaScript, in under a ' +
                    'second, and then cached for days. Everything that makes this game good — a running world, a ' +
                    'socket, a 3D scene — is unavailable to the thing that decides whether anybody clicks. What ' +
                    'is left is an image made on the server, headers that tell the truth about how long an ' +
                    'answer is good for, and a code that still resolves after the world it names has restarted.',
            ],
            build: [
                'POST a moment, get a short code. The moment is small and described by the caller: who, what happened, how long they lasted, which world.',
                'GET the code: an HTML page carrying Open Graph and Twitter card tags, and a preview image generated on the server.',
                'An image that reads at the size a feed actually draws it — on a phone, in a column a third of the screen wide.',
                'The page’s one action is going into the world the moment came from, and it still does something sensible when that world is gone.',
                'A person following the link still arrives in the world the way they do today. The card is for the machine that fetches the link first, never a page that gets in a player’s way.',
                'Codes are short, unguessable enough that nobody walks the space of them, and permanent once minted.',
            ],
            done: [
                'Paste a link into three things that unfurl — a chat app, a social post composer, a link checker — and all three draw the image and the sentence. Screenshots in the delivery.',
                'The preview arrives inside a second, cold, measured from outside. A crawler that gives up draws nothing, and the link is then worse than plain text would have been.',
                'With JavaScript off the page still says what happened and still links into the world.',
                'A player name with markup in it, or three hundred characters of it, cannot break the image or the page. Names are typed by strangers and this renders them into both.',
                'A code minted against a world that no longer exists resolves to a page that says so and offers the way in anyway. Nothing 404s, nothing crashes.',
            ],
            notIncluded: [
                'Video, or capturing a clip. The moment is data, not footage.',
                'Accounts, profiles, or a history of your moments.',
                'Deciding whether a name is allowed. Assume the one that arrives has already been through Blobor’s safety kit.',
            ],
        },
        ar: {
            title: 'الرابط الذي يقول ما الذي حدث',
            summary: 'رابط قصير خلفه لحظة: معاينة تُقرأ داخل التدفّق، وصفحة تُدخل قارئها العالمَ نفسه.',
            why: [
                'خطة الانتشار عند Blobor جملة واحدة: ينبغي للمقطع أن يشرح اللعبة قبل أن يعرف المشاهد اسمها. حين ' +
                    'يُؤكل لاعب تكون اللعبة قد عرفت ما يكفي لقول ذلك: من أكله، وكم صمد، وفي أي عالم. أما ما يُشارَك ' +
                    'اليوم فرابط عارٍ: يجيب blobor.io على رمز المشاركة بتحويل إلى العالم، وهو تصرّف صحيح مع إنسان ' +
                    'ولا شيء البتة مع زاحف — والأصل الحيّ لا يقدّم وسوم بطاقة في أي صفحة. يُلصق الرابط في محادثة ' +
                    'فلا ينشر شيئًا، ويُطلب ممن وصله أن يثق برابط من غريب دون أن يعرف ما على الطرف الآخر منه.',
                'وهي ثلاثة أسابيع لأن الذي تبنون له آلةٌ لن تروها أبدًا. المعاينة يجلبها زاحف يخصّ غيركم، في وقته ' +
                    'هو، بلا جافاسكربت، في أقل من ثانية، ثم يخزّنها أيامًا. وكل ما يجعل هذه اللعبة جيّدة — عالم يعمل، ' +
                    'ومقبس، ومشهد ثلاثي الأبعاد — غير متاح لما سيقرّر إن كان أحد سيضغط. ويبقى: صورة تُصنع على الخادم، ' +
                    'وترويسات تصدق في مدّة صلاحية الجواب، ورمز يظلّ صالحًا بعد أن يكون العالم الذي يسمّيه قد أُعيد ' +
                    'تشغيله.',
            ],
            build: [
                'أرسِلوا لحظة، فتعود إليكم برمز قصير. واللحظة صغيرة يصفها المُرسِل: من، وماذا جرى، وكم صمد، وفي أي عالم.',
                'اطلبوا الرمز: صفحة HTML تحمل وسوم Open Graph وبطاقة Twitter، وصورة معاينة تُولَّد على الخادم.',
                'صورة تُقرأ بالحجم الذي يرسمها به التدفّق فعلًا — على هاتف، في عمود عرضه ثلث الشاشة.',
                'الإجراء الوحيد في الصفحة هو الدخول إلى العالم الذي جاءت منه اللحظة، وتتصرّف تصرّفًا معقولًا حين يكون ذلك العالم قد زال.',
                'ومن يتبع الرابط من البشر يصل إلى العالم كما يصل اليوم. البطاقة للآلة التي تجلب الرابط أولًا، لا صفحةٌ تعترض طريق لاعب.',
                'الرموز قصيرة، ويصعب حزرها بما يمنع المرور على فضائها كلّه، ودائمة بمجرّد إصدارها.',
            ],
            done: [
                'الصقوا الرابط في ثلاثة أشياء تنشر المعاينات — تطبيق محادثة، ومحرّر منشور اجتماعي، وفاحص روابط — فترسم ثلاثتها الصورة والجملة. والصور في التسليم.',
                'تصل المعاينة في أقل من ثانية، على البارد، مقيسةً من الخارج. فالزاحف الذي ييأس لا يرسم شيئًا، ويصير الرابط عندها أسوأ من نصّ عادي.',
                'مع تعطيل جافاسكربت تظل الصفحة تقول ما الذي حدث، وتظل توصل إلى العالم.',
                'اسم لاعب فيه وسوم، أو فيه ثلاثمئة حرف، لا يكسر الصورة ولا الصفحة. الأسماء يكتبها غرباء، وهذا يرسمها في الاثنتين.',
                'رمز صدر لعالم لم يعد قائمًا يفتح صفحة تقول ذلك وتعرض الدخول على أي حال. لا شيء يردّ 404 ولا شيء ينهار.',
            ],
            notIncluded: [
                'الفيديو أو التقاط المقاطع. اللحظة بيانات لا لقطات.',
                'الحسابات والملفات الشخصية وسجلّ لحظاتكم.',
                'الحكم على الاسم إن كان مسموحًا. افترضوا أن الواصل قد مرّ بالفعل على أدوات السلامة عند Blobor.',
            ],
        },
    },
    {
        slug: 'blobor-asset-pipeline',
        client: 'blobor',
        terms: { fee: { minorUnits: 20_000, currency: 'USD' } },
        kind: 'cli',
        weeks: 2,
        posted: '2026-09-19',
        closes: '2026-11-30',
        team: { min: 2, max: 4 },
        shot: {
            path: '',
            en: 'Two browsers on the same world, one holding a cached texture from a version that was quietly overwritten.',
            ar: 'متصفّحان على العالم نفسه، وفي أحدهما نسيج مخزَّن من إصدار كُتب فوقه بصمت.',
        },
        given: [
            {
                en: 'The manifest format, with the live one as a worked example: its version, its prefix, and an entry for every logical name. Invent your own shape and it will publish to a bucket the game cannot read.',
                ar: 'صيغة البيان، ومعها البيان الحيّ مثالًا معمولًا: إصداره وبادئته ومدخلة لكل اسم منطقي. واخترعوا بنيتكم الخاصة فستنشرون إلى دلو لا تستطيع اللعبة قراءته.',
            },
            {
                en: 'The prefix scheme and the cache headers each kind of object is served with, because immutability is half a naming rule and half a header.',
                ar: 'مخطّط البادئة وترويسات التخزين التي يُقدَّم بها كل نوع من الكائنات، فالثبات نصفه قاعدة تسمية ونصفه ترويسة.',
            },
            {
                en: 'A bucket of your own to publish into. Nothing you write ever points at the one the game reads until somebody at Blobor runs it themselves.',
                ar: 'دلو خاص بكم تنشرون إليه. ولا شيء تكتبونه يشير إلى الدلو الذي تقرؤه اللعبة حتى يشغّله أحد من Blobor بنفسه.',
            },
        ],
        sources: [
            {
                mission: 'l86-same-build-different-place',
                en: 'The same input twice has to produce the same output, or nothing below it can be trusted.',
                ar: 'المُدخَل نفسه مرّتين يجب أن يعطي المُخرَج نفسه، وإلا لم يعد ما تحته جديرًا بالثقة.',
            },
            {
                mission: 'l40-migrate-the-records',
                en: 'Half a publish is the state this command exists to make impossible.',
                ar: 'النشر الناقص هو الحالة التي وُجد هذا الأمر ليجعلها مستحيلة.',
            },
            {
                mission: 'l82-code-nobody-remembers',
                en: 'Why a version that can change is not a version.',
                ar: 'لماذا الإصدار الذي يمكن أن يتغيّر ليس إصدارًا.',
            },
            {
                mission: 'l86-same-build-different-place',
                en: 'The same bytes, addressed the same way, from anywhere.',
                ar: 'البايتات نفسها، بالعنونة نفسها، من أي مكان.',
            },
        ],
        en: {
            title: 'A command that publishes the world’s heavy bytes',
            summary: 'Models, textures and audio into a versioned prefix, with a manifest the client can trust and a way back.',
            why: [
                'The game draws its geometry procedurally today, which is why the asset manifest is three lines ' +
                    'with nothing in it. That ends the first time somebody makes a model. When it does, the rule ' +
                    'is absolute and it is not a preference: heavy bytes are immutable and versioned, and a ' +
                    'missing asset must never stop the simulation — the client shows a placeholder and retries. ' +
                    'Both halves of that are properties of how the bytes were published, not of the game code, ' +
                    'and the game is live: the first publish that gets it wrong is one people are standing in.',
                'Two weeks because the hard part is not uploading. It is that a published version can never ' +
                    'change. A browser holding v42 in its cache and a browser opening for the first time have to ' +
                    'be looking at the same world, and the moment one texture is overwritten in place, one of ' +
                    'them is wrong and nobody can tell which one. So the command has to make overwriting ' +
                    'impossible rather than merely avoid doing it.',
            ],
            build: [
                'One command over a directory: hash every file, put each one under a versioned prefix keyed by its content, and write the manifest that maps logical names to those objects.',
                'A dry run that says exactly what would be uploaded, what is already there, and how much of it there is.',
                'Refusing to overwrite anything already published, and naming the file that collided.',
                'Rolling back is publishing an older manifest, in one step, with no upload.',
                'One page of README: how to publish, how to roll back, and what a manifest entry means.',
            ],
            done: [
                'Publishing the same directory twice produces byte-identical manifests and uploads nothing the second time.',
                'A changed file produces a new object and leaves the old one untouched. The previous manifest still resolves completely, every entry of it.',
                'It never half-publishes. The manifest is written only once every object it names is there, and an interrupted run leaves the last good version intact and says what it did.',
                'Somebody who has never seen it publishes and rolls back from the README alone in under ten minutes. Watch a person do it; do not estimate it.',
            ],
            notIncluded: [
                'Compressing, converting or optimising any asset. Publish what you are given.',
                'The client half. Loading, prefetching and the placeholder fallback are Blobor’s.',
                'A user interface. This is a command, run by a person or by a pipeline.',
            ],
        },
        ar: {
            title: 'أمر ينشر بايتات العالم الثقيلة',
            summary: 'مجسّمات ونسيج وصوت إلى بادئة مُصدَّرة، مع بيان يثق به العميل وطريق للرجوع.',
            why: [
                'ترسم اللعبة هندستها إجرائيًا اليوم، ولذلك فبيان الأصول ثلاثة أسطر لا شيء فيها. وينتهي ذلك أول ' +
                    'مرّة يصنع فيها أحدهم مجسّمًا. وحين ينتهي تكون القاعدة قاطعة لا مفاضلة فيها: البايتات الثقيلة ' +
                    'ثابتة ومُصدَّرة، والأصل المفقود يجب ألّا يوقف المحاكاة — يعرض العميل بديلًا ويعيد المحاولة. ' +
                    'وكلا النصفين خاصّية في طريقة نشر البايتات لا في شيفرة اللعبة. واللعبة حيّة الآن: فأول نشرة ' +
                    'تخطئ في هذا سيقف الناس داخلها.',
                'وهو أسبوعان لأن الصعب ليس الرفع. الصعب أن الإصدار المنشور لا يجوز أن يتغيّر أبدًا. متصفّح يحمل ' +
                    'الإصدار 42 في مخزنه ومتصفّح يفتح للمرة الأولى يجب أن يكونا ينظران إلى العالم نفسه، ولحظة يُكتب ' +
                    'فوق نسيج واحد في مكانه يصير أحدهما مخطئًا ولا أحد يعرف أيّهما. فعلى الأمر أن يجعل الكتابة فوق ' +
                    'المنشور مستحيلة، لا أن يتجنّبها فحسب.',
            ],
            build: [
                'أمر واحد على مجلّد: يجزّئ كل ملف، ويضع كلًّا منها تحت بادئة مُصدَّرة مفتاحها محتواه، ويكتب البيان الذي يربط الأسماء المنطقية بتلك الكائنات.',
                'تشغيل تجريبي يقول بالضبط ما الذي سيُرفع، وما هو موجود أصلًا، وكم حجمه.',
                'رفض الكتابة فوق أي شيء منشور، مع تسمية الملف المتعارض.',
                'الرجوع نشرُ بيان أقدم، بخطوة واحدة، وبلا رفع.',
                'صفحة README واحدة: كيف يُنشر، وكيف يُرجَع، وما معنى مدخلة في البيان.',
            ],
            done: [
                'نشر المجلّد نفسه مرّتين ينتج بيانين متطابقين بايتًا ببايت، ولا يرفع شيئًا في المرّة الثانية.',
                'الملف المتغيّر ينتج كائنًا جديدًا ويترك القديم كما هو. والبيان السابق يظل يُحلّ بالكامل، بكل مدخلة فيه.',
                'لا ينشر نصف نشرة أبدًا. لا يُكتب البيان إلا بعد أن يصير كل كائن يسمّيه موجودًا، والتشغيل المقطوع يترك آخر إصدار سليم كما هو ويقول ما الذي فعله.',
                'من لم يره قطّ ينشر ويرجع من README وحدها في أقل من عشر دقائق. راقبوا إنسانًا يفعلها؛ ولا تقدّروها تقديرًا.',
            ],
            notIncluded: [
                'الضغط أو التحويل أو تحسين أي أصل. انشروا ما يُعطى لكم.',
                'النصف الذي في العميل. التحميل والجلب المسبق والبديل الاحتياطي شأن Blobor.',
                'واجهة رسومية. هذا أمر يشغّله إنسان أو خط إنتاج.',
            ],
        },
    },
];
/**
 * The longest note an entry may carry, counted in code points.
 *
 * Here rather than in the Worker because both halves have to agree: the field
 * stops the reader where the server would refuse them, and a limit written
 * down twice is a limit that eventually differs by language — which on this
 * product means the Arabic box is the one that silently gets shorter.
 *
 * Code points, not UTF-16 units, for the same reason: `.length` counts an
 * emoji as two and most Arabic as one, so the cheap measure would hand the two
 * languages different allowances for the same sentence.
 */
export const MAX_PITCH = 600;
/**
 * Where a team says it has got to, from a closed list.
 *
 * A vocabulary rather than free text, for the reason the analytics event names
 * are one (docs/analytics.md): these values are drawn beside each other, in
 * two languages, and compared by a reader deciding whether to start. Free text
 * would make "nearly done" and "almost finished" two different states to
 * everybody reading the board and one state to the person who typed them.
 *
 * **"Submitted" is deliberately not in this list.** Whether a team has
 * delivered is a fact the database already holds, and a status somebody sets
 * by hand beside a log the server writes is a status that will eventually
 * disagree with it. The page derives that word from the submissions and never
 * from here.
 *
 * Everything in this list is a claim a team makes about itself. Nothing checks
 * it, and every surface that draws it says so — this product does not present
 * a claim as a checked fact, and the one place it would be tempting to start
 * is the one where a team is competing for money.
 */
export const TEAM_PROGRESS = ['joined', 'building', 'stopped'];
/** Is this one of ours? The Worker's gate on the value in a request body. */
export function isTeamProgress(value) {
    return typeof value === 'string' && TEAM_PROGRESS.includes(value);
}
/**
 * The longest a delivery link and its note may be, in code points.
 *
 * Code points for `MAX_PITCH`'s reason: `.length` counts most Arabic as one
 * unit and an emoji as two, so the cheap measure hands the two languages
 * different allowances for the same sentence.
 *
 * The note is longer than the pitch because it carries the team's own answer
 * to the `done` list, condition by condition — which is the thing a reviewer
 * reads first and the thing the team spent an hour agreeing
 * (docs/projects-review.md).
 */
export const MAX_DELIVERY_URL = 300;
export const MAX_DELIVERY_NOTE = 2000;
/**
 * How near the deadline "closing soon" starts, in whole days.
 *
 * A week, because that is roughly the last moment at which forming a team and
 * starting is still a real option. The pill it drives is a courtesy to a
 * reader scanning the board, never a rule: `projectOpen` is what the Worker
 * asks, and a project that is "closing soon" is open in every way that counts.
 */
export const CLOSING_SOON_DAYS = 7;
/** The board, by slug. Built once; the board is a constant. */
const BY_SLUG = new Map(PROJECTS.map((p) => [p.slug, p]));
/**
 * Is this string one of ours?
 *
 * The Worker's gate on a slug, and the reason the route needs no table of its
 * own to validate against: a slug that is not on the board cannot have an
 * entry written for it.
 */
export function isProjectSlug(value) {
    return typeof value === 'string' && BY_SLUG.has(value);
}
/** One project, or null. Never throws on an unknown slug — a bad link is a
 *  page that says so, not a crash. */
export function projectBySlug(slug) {
    return BY_SLUG.get(slug) ?? null;
}
/**
 * The brief in one language.
 *
 * A function rather than an index, so every caller goes through the same
 * fallback: an unknown locale reads English rather than rendering `undefined`
 * into a heading.
 */
export function projectCopy(project, locale) {
    return locale === 'ar' ? project.ar : project.en;
}
/** The same fallback, for the values that are a pair rather than a whole
 *  brief: a source's note, an image's caption. */
export function projectText(text, locale) {
    return locale === 'ar' ? text.ar : text.en;
}
/** Who posted this brief. Never null: `client` is a literal union, so every
 *  project has a company and the map cannot miss. */
export function projectClient(project) {
    return CLIENT_BY_ID.get(project.client);
}
/** Is this brief on the board right now? False while its company's briefs
 *  are coming soon — see `Client.comingSoon`. */
export function projectPosted(project) {
    return !projectClient(project).comingSoon;
}
/** One company by id, or null for a string that is not one of ours. */
export function clientById(id) {
    return CLIENT_BY_ID.get(id) ?? null;
}
/**
 * The terms on a brief, or null where there are none to have.
 *
 * Null and a `Terms` with empty halves are different answers and every caller
 * has to tell them apart: null is Lattice, where there is no fee because
 * nobody is buying anything, and an unsettled `Terms` is a real client who has
 * not finished deciding. Drawing the second as the first would tell a reader
 * that paid work is practice.
 */
export function projectTerms(project) {
    return project.terms ?? null;
}
/**
 * Is this brief waiting on terms somebody has not decided yet?
 *
 * True only for a paid brief with no fee. A practice brief has no terms to
 * settle and is never waiting on anything, which is why this asks about the
 * fee rather than about the presence of a `terms` object.
 */
export function termsPending(project) {
    return !!project.terms && !project.terms.fee;
}
/** Is somebody paying for this, and has the figure been decided? */
export function projectPaid(project) {
    return !!project.terms?.fee;
}
/**
 * Are entries still open, at this instant?
 *
 * Two conditions, and the second one is why this function exists rather than
 * a date comparison at each call site.
 *
 * **The deadline.** `closes` is a date and it runs to the end of that day UTC,
 * so this adds the day rather than comparing timestamps at midnight — a
 * closing date that shut the door at 00:00 would cost every team the day they
 * thought they had.
 *
 * **The terms.** A paid brief with no fee takes no entries. It is posted, it
 * is readable, and the door is shut: a team cannot commit to three weeks of
 * work for a price nobody has set. That rule lives here, in the one function
 * the Worker calls at the press (worker/projects.ts) and the one the page
 * derives every control from, so it cannot be enforced in the browser and
 * forgotten on the server.
 *
 * The clock is passed in rather than read, because the Worker decides this and
 * the browser only draws it, and the two must be able to disagree without the
 * browser being the one that is believed.
 */
export function projectOpen(project, now) {
    if (termsPending(project))
        return false;
    const end = Date.parse(`${project.closes}T23:59:59.999Z`);
    return Number.isFinite(end) && now <= end;
}
/**
 * The status, derived and never stored.
 *
 * One function, so a pill on a card, the chips at the top of a brief and the
 * control at the bottom of it cannot disagree — the bug this replaces is a
 * board that says "Open" over a button that refuses. `soon` is `open` with a
 * week left; everything a caller may *do* is decided by `projectOpen`, and
 * this only decides what a reader is told.
 *
 * `pending` is the fourth word and it is checked first, because "closed" and
 * "not open yet" are opposite facts and a reader told the wrong one goes away.
 * A brief whose terms are still being settled has not shut; it has not
 * started.
 */
export function projectStatus(project, now) {
    if (termsPending(project))
        return 'pending';
    if (!projectOpen(project, now))
        return 'closed';
    const end = Date.parse(`${project.closes}T23:59:59.999Z`);
    return end - now <= CLOSING_SOON_DAYS * 86_400_000 ? 'soon' : 'open';
}
//# sourceMappingURL=projects.js.map